import React, { forwardRef } from 'react';
import { format } from 'date-fns';

// Payment status configuration
const statusConfig = {
    paid: {
        label: 'PAID',
        color: '#16a34a', // green-600
        bgColor: 'rgba(22, 163, 74, 0.08)',
        showWatermark: false,
        badgeBg: '#dcfce7', // green-100
        badgeText: '#15803d' // green-700
    },
    partial: {
        label: 'PARTIAL',
        color: '#d97706', // amber-600
        bgColor: 'rgba(217, 119, 6, 0.12)',
        showWatermark: true,
        badgeBg: '#fef3c7', // amber-100
        badgeText: '#b45309' // amber-700
    },
    unpaid: {
        label: 'UNPAID',
        color: '#dc2626', // red-600
        bgColor: 'rgba(220, 38, 38, 0.15)',
        showWatermark: true,
        badgeBg: '#fee2e2', // red-100
        badgeText: '#b91c1c' // red-700
    },
    draft: {
        label: 'DRAFT',
        color: '#6366f1', // indigo-500
        bgColor: 'rgba(99, 102, 241, 0.12)',
        showWatermark: true,
        badgeBg: '#e0e7ff', // indigo-100
        badgeText: '#4338ca' // indigo-700
    }
};

/**
 * Thermal / Terminal receipt layout (58mm–80mm POS printers).
 * Fixed-width monospace, pure black on white for clean thermal printing.
 */
const TerminalReceipt = forwardRef(({ sale, businessDetails, paymentStatus = 'paid' }, ref) => {
    if (!sale) return null;

    const { Customer, SaleItems, total, tax, discount, createdAt, order_number } = sale;

    // Header/contact details: prefer the business record, fall back to the shop
    // the sale was made at — so the contact block always shows something.
    const shop = sale.shop || sale.Shop || null;
    const bd = businessDetails || {};
    // address/contact are JSON columns but can come back as raw strings (they are
    // stored as TEXT on some databases) — parse defensively so street/phone show.
    const parseMaybe = (v) => {
        if (!v) return {};
        if (typeof v === 'string') { try { return JSON.parse(v) || {}; } catch { return {}; } }
        return v;
    };
    const hasVal = (o) => o && Object.values(o).some((v) => v && String(v).trim());
    const bAddr = parseMaybe(bd.address);
    const sAddr = parseMaybe(shop?.address);
    const bContact = parseMaybe(bd.contact);
    const sContact = parseMaybe(shop?.contact);
    const name = bd.name || shop?.name || 'Your Company';
    const address = hasVal(bAddr) ? bAddr : sAddr;
    const contact = hasVal(bContact) ? bContact : sContact;
    const currency = bd.currency || 'GHS';
    const currencySymbol = currency === 'GHS' ? 'GH₵' : currency;

    // Compose the address into one line ("123 Main Street, Accra") like the photo.
    const addressLine = [address?.street, address?.city, address?.state]
        .filter(Boolean).join(', ') + (address?.zip ? ` ${address.zip}` : '');

    const subtotal = parseFloat(total || 0) - parseFloat(tax || 0) + parseFloat(discount || 0);
    const amountPaid = parseFloat(sale.amount_paid || 0);
    const saleStatus = (sale.status || paymentStatus || 'completed').toUpperCase();
    const payMethod = (sale.method || 'CASH').toUpperCase();

    // Cashier: prefer full name, fall back to username.
    const u = sale.User || sale.user || null;
    const cashier = u
        ? (`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || '')
        : '';
    const location = shop?.name || bd.name || '';

    const money = (v) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const dashed = { borderTop: '1px dashed #000', margin: '6px 0' };

    // Info rows (label left, value right); skip empty ones.
    const infoRows = [
        ['Receipt #', order_number || '---'],
        ['Date', format(new Date(createdAt || Date.now()), 'yyyy-MM-dd')],
        ['Time', format(new Date(createdAt || Date.now()), 'HH:mm:ss')],
        ['Cashier', cashier],
        ['Location', location],
        ['Customer', Customer?.name || ''],
    ].filter(([, v]) => v !== '' && v !== null && v !== undefined);

    return (
        <div ref={ref} style={{
            width: '280px',
            padding: '10px 8px',
            backgroundColor: 'white',
            color: '#000',
            fontSize: '13px',
            fontFamily: "'Courier New', Courier, monospace",
            fontWeight: '600',
            margin: '0 auto',
            lineHeight: 1.4,
        }}>
            {/* Business / contact header */}
            <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                <p style={{ fontSize: '18px', fontWeight: '800', margin: '0 0 4px 0', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                    {name}
                </p>
                {addressLine && <p style={{ margin: 0, fontSize: '12px' }}>{addressLine}</p>}
                {contact?.phone && <p style={{ margin: 0, fontSize: '12px' }}>Tel: {contact.phone}</p>}
                {contact?.email && <p style={{ margin: 0, fontSize: '12px' }}>{contact.email}</p>}
            </div>

            <div style={dashed} />

            {/* Receipt info */}
            <div style={{ fontSize: '12px' }}>
                {infoRows.map(([label, value]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '2px' }}>
                        <span>{label}:</span>
                        <span style={{ textAlign: 'right' }}>{value}</span>
                    </div>
                ))}
            </div>

            <div style={dashed} />

            {/* Items table header */}
            <div style={{ display: 'flex', fontWeight: '800', fontSize: '12px', marginBottom: '4px' }}>
                <span style={{ flex: 1 }}>ITEM</span>
                <span style={{ width: '28px', textAlign: 'center' }}>QTY</span>
                <span style={{ width: '62px', textAlign: 'right' }}>PRICE</span>
                <span style={{ width: '66px', textAlign: 'right' }}>TOTAL</span>
            </div>
            <div style={{ borderTop: '1px solid #000', marginBottom: '4px' }} />

            {/* Items */}
            {SaleItems?.map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'flex-start', fontSize: '12px', marginBottom: '4px' }}>
                    <span style={{ flex: 1, paddingRight: '4px', wordBreak: 'break-word' }}>
                        {item.title || item.name || item.product?.title || item.product?.name || 'Unknown'}
                    </span>
                    <span style={{ width: '28px', textAlign: 'center' }}>{item.qty}</span>
                    <span style={{ width: '62px', textAlign: 'right' }}>{money(item.product_price)}</span>
                    <span style={{ width: '66px', textAlign: 'right', fontWeight: '700' }}>{money(item.total)}</span>
                </div>
            ))}

            <div style={dashed} />

            {/* Subtotal / tax / discount */}
            <div style={{ fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                    <span>Subtotal</span>
                    <span>{money(subtotal)}</span>
                </div>
                {Array.isArray(sale.taxes) && sale.taxes.length > 0
                    ? sale.taxes.map((t, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                            <span>{t.name} {Number(t.rate)}%{t.is_inclusive ? ' (incl.)' : ''}</span>
                            <span>{money(t.amount)}</span>
                        </div>
                    ))
                    : parseFloat(sale.tax || 0) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                            <span>Tax</span>
                            <span>{money(sale.tax)}</span>
                        </div>
                    )}
                {parseFloat(sale.discount || 0) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                        <span>Discount</span>
                        <span>-{money(sale.discount)}</span>
                    </div>
                )}
            </div>

            <div style={dashed} />

            {/* Grand total + paid */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontWeight: '900', fontSize: '17px', margin: '4px 0' }}>
                <span>TOTAL</span>
                <span>{currencySymbol} {money(sale.total)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                <span>Paid</span>
                <span>{money(amountPaid)}</span>
            </div>

            <div style={dashed} />

            {/* Payment method + status */}
            <p style={{ textAlign: 'center', fontWeight: '800', fontSize: '12px', margin: '4px 0', letterSpacing: '0.5px' }}>
                {payMethod} | Status: {saleStatus}
            </p>

            <div style={dashed} />

            {/* Footer */}
            <div style={{ textAlign: 'center', marginTop: '6px', fontSize: '12px' }}>
                <p style={{ margin: '4px 0', fontWeight: '800', letterSpacing: '1px' }}>THANK YOU</p>
                <p style={{ margin: '2px 0' }}>{bd.receipt_notes || 'Goods sold are not returnable'}</p>
                <p style={{ margin: '2px 0' }}>Please retain receipt as proof of purchase</p>
                {/* <p style={{ margin: '6px 0 0 0', fontSize: '11px' }}>Bayorwor: +233553826524</p> */}
            </div>
        </div>
    );
});

TerminalReceipt.displayName = 'TerminalReceipt';

/**
 * Normal / A4 receipt layout (standard printers)
 */
const NormalReceipt = forwardRef(({ sale, businessDetails, paymentStatus = 'paid' }, ref) => {
    if (!sale) return null;

    const { Customer, SaleItems, total, tax, discount, createdAt, order_number } = sale;
    const { name, address, contact, tax_rate, currency, logo_url, receipt_notes } = businessDetails || {};

    // Currency symbol/code
    const currencyCode = currency || 'GHS';

    // Calculate subtotal from total, tax, discount
    const subtotal = parseFloat(total || 0) - parseFloat(tax || 0) + parseFloat(discount || 0);

    // Get status configuration
    const status = statusConfig[paymentStatus] || statusConfig.paid;

    return (
        <div ref={ref} style={{
            padding: '40px',
            backgroundColor: 'white',
            color: 'black',
            fontSize: '14px',
            fontFamily: 'Inter, Arial, sans-serif',
            maxWidth: '800px',
            margin: '0 auto',
            position: 'relative'
        }}>
            {/* Status Watermark */}
            {status.showWatermark && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-35deg)',
                    fontSize: '80px',
                    fontWeight: '900',
                    color: status.bgColor,
                    textTransform: 'uppercase',
                    letterSpacing: '10px',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    zIndex: 10
                }}>
                    {status.label}
                </div>
            )}
            {/* Top Section: Company Info (Left) & Logo/Actions (Right) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px' }}>
                {/* Company Info */}
                <div>
                    <h1 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', margin: 0, color: '#111827' }}>
                        {name || "Your Company Inc."}
                    </h1>
                    <div style={{ color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
                        <p style={{ margin: 0 }}>{address?.street || "1234 Company St,"}</p>
                        <p style={{ margin: 0 }}>{address?.city}{address?.state ? `, ${address.state}` : ''} {address?.zip}</p>
                        {contact?.phone && <p style={{ margin: 0 }}>{contact.phone}</p>}
                    </div>
                </div>

                {/* Logo Area */}
                <div style={{ textAlign: 'right' }}>
                    {logo_url ? (
                        <img src={logo_url} alt="Logo" style={{ height: '60px', width: 'auto' }} />
                    ) : (
                        <div style={{
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            padding: '12px 20px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: '#1E3A5F',
                            backgroundColor: '#fff'
                        }}>
                            {/* Cloud upload icon can go here if needed, keeping simple text for print */}
                            <span style={{ fontWeight: '500' }}>☁ Upload Logo</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Title & Receipt Details Section */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '40px' }}>
                {/* Billed To (Left) */}
                <div style={{ width: '50%' }}>
                    <h3 style={{
                        color: '#1E3A5F',
                        fontSize: '14px',
                        fontWeight: '700',
                        marginBottom: '8px',
                        margin: 0
                    }}>Billed To</h3>
                    <div style={{ fontSize: '16px', color: '#111827' }}>
                        <p style={{ fontWeight: '500', margin: '0 0 4px 0' }}>{Customer?.name || sale.billing_fname || "Customer Name"}</p>
                        <div style={{ color: '#374151', fontSize: '14px' }}>
                            {/* Using some dummy address format if not present to match the look, or real data */}
                            {Customer?.address ? <p style={{ margin: '0' }}>{Customer.address}</p> : <p style={{ margin: '0' }}>1234 Customer St,<br />Customer Town, ST 12345</p>}
                        </div>
                    </div>
                </div>

                {/* Title & Details (Right) */}
                <div style={{ textAlign: 'right', width: '50%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', marginBottom: '32px' }}>
                        <h2 style={{
                            fontSize: '32px',
                            fontWeight: '700',
                            color: '#003366',
                            textTransform: 'uppercase',
                            letterSpacing: '2px',
                            margin: '0'
                        }}>Sales Receipt</h2>
                    </div>

                    {/* Receipt Details Grid */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '240px' }}>
                            <span style={{ fontWeight: '700', color: '#003366' }}>Receipt #</span>
                            <span style={{ fontFamily: 'monospace', fontWeight: '500' }}>{order_number || "0000457"}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '240px' }}>
                            <span style={{ fontWeight: '700', color: '#003366' }}>Receipt date</span>
                            <span style={{ fontWeight: '500' }}>{createdAt ? format(new Date(createdAt), 'dd-MM-yyyy') : format(new Date(), 'dd-MM-yyyy')}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '240px' }}>
                            <span style={{ fontWeight: '700', color: '#003366' }}>Status</span>
                            <span style={{ fontWeight: '600', color: status.color }}>{status.label}</span>
                        </div>
                        {sale.method && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '240px' }}>
                                <span style={{ fontWeight: '700', color: '#003366' }}>Payment Method</span>
                                <span style={{ fontWeight: '500' }}>{sale.method}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Items Table */}
            <table style={{ width: '100%', marginBottom: '24px', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ backgroundColor: '#003366', color: 'white' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', width: '60px', fontWeight: '700', fontSize: '14px' }}>QTY</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', fontSize: '14px' }}>Description</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '700', fontSize: '14px' }}>Unit Price</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '700', fontSize: '14px' }}>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {SaleItems?.map((item, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '16px', textAlign: 'center', fontWeight: '500', color: '#111827' }}>{item.qty}</td>
                            <td style={{ padding: '16px', color: '#111827' }}>{item.title || item.name || item.product?.title || item.product?.name || 'Unknown Product'}</td>
                            <td style={{ padding: '16px', textAlign: 'right', color: '#111827' }}>{parseFloat(item.product_price).toFixed(2)}</td>
                            <td style={{ padding: '16px', textAlign: 'right', color: '#111827' }}>{currencyCode} {parseFloat(item.total).toFixed(2)}</td>
                        </tr>
                    ))}
                    {/* Filling with empty rows if needed to match height, but for dynamic content standard map is fine */}
                </tbody>
            </table>

            {/* Divider Line */}
            <div style={{ borderTop: '2px solid #003366', marginTop: '0', marginBottom: '16px' }}></div>

            {/* Totals Section */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '60px' }}>
                <div style={{ width: '40%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '15px' }}>
                        <span style={{ color: '#111827' }}>Subtotal</span>
                        <span style={{ color: '#111827', fontWeight: '500' }}>{currencyCode} {subtotal.toFixed(2)}</span>
                    </div>
                    {Array.isArray(sale.taxes) && sale.taxes.length > 0
                        ? sale.taxes.map((t, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '15px' }}>
                                <span style={{ color: '#111827' }}>{t.name} ({Number(t.rate)}%){t.is_inclusive ? ' incl.' : ''}</span>
                                <span style={{ color: '#111827', fontWeight: '500' }}>{currencyCode} {Number(t.amount).toFixed(2)}</span>
                            </div>
                        ))
                        : parseFloat(sale.tax || 0) > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '15px' }}>
                                <span style={{ color: '#111827' }}>Sales Tax ({tax_rate || 0}%)</span>
                                <span style={{ color: '#111827', fontWeight: '500' }}>{currencyCode} {parseFloat(sale.tax || 0).toFixed(2)}</span>
                            </div>
                        )}
                    {parseFloat(sale.discount || 0) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '15px', color: '#DC2626' }}>
                            <span>Discount</span>
                            <span>- {currencyCode} {parseFloat(sale.discount).toFixed(2)}</span>
                        </div>
                    )}

                    <div style={{ borderTop: '1px solid #E5E7EB', margin: '8px 0' }}></div>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        color: '#003366',
                        fontSize: '18px',
                        fontWeight: '800'
                    }}>
                        <span>Total ({currencyCode})</span>
                        <span>{currencyCode} {parseFloat(sale.total).toFixed(2)}</span>
                    </div>
                    <div style={{ borderTop: '2px solid #003366', marginTop: '12px' }}></div>
                </div>
            </div>

            {/* Footer / Notes */}
            <div>
                <h4 style={{ fontWeight: '700', color: '#003366', marginBottom: '8px', margin: 0, fontSize: '14px' }}>Notes</h4>
                <p style={{ color: '#374151', margin: '4px 0', fontSize: '14px' }}>{receipt_notes || "Thank you for your business!"}</p>
            </div>
        </div>
    );
});

NormalReceipt.displayName = "NormalReceipt";

/**
 * Receipt component that switches layout based on businessDetails.printer_type
 */
const Receipt = forwardRef(({ sale, businessDetails, paymentStatus = 'paid' }, ref) => {
    const printerType = businessDetails?.printer_type || 'normal';

    if (printerType === 'terminal') {
        return <TerminalReceipt ref={ref} sale={sale} businessDetails={businessDetails} paymentStatus={paymentStatus} />;
    }

    return <NormalReceipt ref={ref} sale={sale} businessDetails={businessDetails} paymentStatus={paymentStatus} />;
});

Receipt.displayName = "Receipt";
export default Receipt;
